import { PlottingSchoolTypeEnum } from "../_enums/plotting-filters.enum";

import type { PlottingFiltersFormValues } from "../_schemas/plotting-filters.schema";

export const NO_ATTENDANCE_SCHOOL_INFO_SENTINEL = "__no_attendance_school_info__";
export const ORPHAN_ATTENDANCE_SCHOOL_INTERNAL_ID_PREFIX = "__internal_id__:";

export interface ResolvedPlottingSchoolFilterParams {
  schoolType: string;
  attendingSchoolIds?: string[];
}

export interface ResolvedPlottingSchoolTegolaParams {
  schoolType?: string;
  attendingSchoolIds: string[];
  attendingSchoolIdsParam: string;
}

type SchoolFilterInput = Pick<PlottingFiltersFormValues, "schoolType" | "attendingSchoolIds">;

export function buildOrphanAttendanceSchoolSentinel(internalId: string): string {
  return `${ORPHAN_ATTENDANCE_SCHOOL_INTERNAL_ID_PREFIX}${internalId}`;
}

export function getOrphanAttendanceSchoolInternalId(value: string): string | null {
  if (!value.startsWith(ORPHAN_ATTENDANCE_SCHOOL_INTERNAL_ID_PREFIX)) {
    return null;
  }

  const internalId = value.slice(ORPHAN_ATTENDANCE_SCHOOL_INTERNAL_ID_PREFIX.length).trim();
  return internalId || null;
}

export function formatAttendingSchoolIdsTegolaParam(ids: string[]): string {
  return ids.length > 0 ? ids.join(",") : "ALL";
}

export function resolvePlottingSchoolFilterParams(
  filters: SchoolFilterInput,
): ResolvedPlottingSchoolFilterParams {
  const ids = filters.attendingSchoolIds ?? [];

  if (filters.schoolType === PlottingSchoolTypeEnum.INDIVIDUAL) {
    return {
      schoolType: "ALL",
      attendingSchoolIds: ids.length > 0 ? ids : undefined,
    };
  }

  if (filters.schoolType === PlottingSchoolTypeEnum.ALL) {
    return { schoolType: "ALL" };
  }

  return { schoolType: filters.schoolType };
}

export function resolvePlottingSchoolTegolaParams(
  filters: SchoolFilterInput,
): ResolvedPlottingSchoolTegolaParams {
  const { schoolType, attendingSchoolIds } = resolvePlottingSchoolFilterParams(filters);
  const ids = attendingSchoolIds ?? [];

  if (ids.length > 0) {
    return {
      attendingSchoolIds: ids,
      attendingSchoolIdsParam: formatAttendingSchoolIdsTegolaParam(ids),
    };
  }

  if (schoolType !== "ALL") {
    return {
      schoolType,
      attendingSchoolIds: [],
      attendingSchoolIdsParam: "ALL",
    };
  }

  return { attendingSchoolIds: [], attendingSchoolIdsParam: "ALL" };
}

export interface AttendanceSchoolImportOptions {
  orphanInternalIds: string[];
  hasNoAttendanceSchoolInfo: boolean;
}

export function pruneAttendingSchoolIdsForImportOptions(
  selectedIds: string[],
  options: AttendanceSchoolImportOptions,
): string[] {
  const validOrphanIds = new Set(
    options.orphanInternalIds.map((internalId) => buildOrphanAttendanceSchoolSentinel(internalId)),
  );

  return selectedIds.filter((id) => {
    if (getOrphanAttendanceSchoolInternalId(id) !== null) {
      return validOrphanIds.has(id);
    }

    if (id === NO_ATTENDANCE_SCHOOL_INFO_SENTINEL) {
      return options.hasNoAttendanceSchoolInfo;
    }

    return true;
  });
}
